import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    MenuItem,
    Typography,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Tooltip,
} from "@mui/material";
import { Download } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { useTablePagination, useAppMeta } from "hooks";
import { exportExcelFile } from "utils/exportExcelFile";

import { useLazyGetBranchAvgDeliveryTimeQuery, useLazyGetRegionAvgDeliveryTimeQuery } from "store/services/reportApi";
import { useLazyGetDdlRegionListQuery } from "store/services/LocationsApi/regionApi";
import { useNavigate, useSearchParams } from "react-router-dom";

const formatDuration = (ms) => {
    if (ms === null || ms === undefined) return "N/A";
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0) parts.push(`${seconds}s`);
    return parts.length ? parts.join(" ") : "0s";
};

const ShipmentDeliveryReport = () => {
    useAppMeta("Shipment Delivery Report");

    const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const keyword = searchParams.get("search") || "";
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [fetchRegions, { data: regions = [], isLoading: regionsLoading }] =
        useLazyGetDdlRegionListQuery();

    const [fetchBranchAvgExport] = useLazyGetBranchAvgDeliveryTimeQuery();

    useEffect(() => { fetchRegions(); }, [fetchRegions]);

    const selectedRegion = regions.find((r) => r._id === searchParams.get("regionId")) || regions[0] || null;

    const [
        fetchRegionAvg,
        { data: regionAvgData, isLoading: regionAvgLoading, isFetching: regionAvgFetching },
    ] = useLazyGetRegionAvgDeliveryTimeQuery();

    useEffect(() => {
        if (!selectedRegion?._id) return;
        fetchRegionAvg({
            regionId: selectedRegion._id,
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
        });
    }, [selectedRegion, startDate, endDate, fetchRegionAvg]);

    const [
        fetchBranchAvg,
        { data: branchData, isLoading: branchLoading, isFetching: branchFetching, error },
    ] = useLazyGetBranchAvgDeliveryTimeQuery();

    useEffect(() => {
        if (!selectedRegion?._id) return;
        fetchBranchAvg({
            regionId: selectedRegion._id,
            page: currentPage,
            limit: perPage,
            ...(keyword && { keyword }),
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
        });
    }, [selectedRegion, currentPage, perPage, keyword, startDate, endDate, fetchBranchAvg]);

    const columns = [
        {
            name: "Branch Name",
            selector: (row) => row.name,
            cell: (row) => (
                <span
                    style={{ cursor: "pointer", color: "#1976d2", fontWeight: 500 }}
                    onClick={() =>
                        navigate(
                            `/app/crm/reports/average-delivery-time/branch/${row._id}?${startDate ? `startDate=${startDate}&` : ""}${endDate ? `endDate=${endDate}` : ""}`
                        )
                    }
                >
                    {row.name || "N/A"}
                </span>
            ),
            sortable: true,
        },
        {
            name: "Branch Code",
            selector: (row) => row.code,
            cell: (row) => row.code || "N/A",
        },
        {
            name: "Total Delivered",
            selector: (row) => row.totalShipments,
            cell: (row) => row.totalShipments ?? 0,
            sortable: true,
        },
        {
            name: "Avg Delivery Time",
            selector: (row) => row.avgDeliveryTime,
            cell: (row) =>
                row.avgDeliveryTime !== null ? (
                    <Chip
                        label={formatDuration(row.avgDeliveryTime)}
                        size="small"
                        style={{ backgroundColor: "#DFF7DF", color: "green", fontWeight: "bold", fontSize: "0.7rem" }}
                    />
                ) : (
                    <Chip
                        label="No Data"
                        size="small"
                        style={{ backgroundColor: "#F8F9F9", color: "#7F8C8D", fontSize: "0.7rem" }}
                    />
                ),
            sortable: true,
        },
    ];

    const handleExcelExport = async () => {
        try {
            const response = await fetchBranchAvgExport({
                regionId: selectedRegion._id,
                exportAll: "true",
                ...(keyword && { keyword }),
                ...(startDate && { startDate }),
                ...(endDate && { endDate }),
            }).unwrap();

            const exportData = response?.data?.map((row) => ({
                "Branch Name": row.name || "N/A",
                "Branch Code": row.code || "N/A",
                "Total Delivered Shipments": row.totalShipments ?? 0,
                "Avg Delivery Time": formatDuration(row.avgDeliveryTime),
            }));

            exportExcelFile({
                sheetData: exportData,
                fileName: `Shipment_Delivery_Report_${selectedRegion?.name}_${startDate || "All"}_to_${endDate || "All"}`,
            });
        } catch (err) {
            console.error("Export failed:", err);
        }
    };

    const breadcrumbs = [
        { title: "Reports", path: "/app/crm/reports/list" },
        { title: "Shipment Delivery Time" },
    ];

    const isLoadingRegionAvg = regionAvgLoading || regionAvgFetching;

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Error error={error} />
            <BreadcrumbLayout.Paper>

                <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>

                    <TextField
                        select
                        label="Select Region"
                        value={selectedRegion?._id || ""}
                        onChange={(e) => {
                            const newParams = new URLSearchParams(searchParams);
                            newParams.set("regionId", e.target.value);
                            setSearchParams(newParams);
                        }}
                        size="small"
                        sx={{ minWidth: 200 }}
                        disabled={regionsLoading}
                    >
                        {regions.map((r) => (
                            <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>
                        ))}
                    </TextField>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 0.75,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            flexWrap: "wrap",
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            <strong>{selectedRegion?.name || "—"}</strong> avg:
                        </Typography>
                        {isLoadingRegionAvg ? (
                            <CircularProgress size={14} />
                        ) : (
                            <>
                                <Chip
                                    label={formatDuration(regionAvgData?.avgDeliveryTime)}
                                    size="small"
                                    style={{ backgroundColor: "#DFF7DF", color: "green", fontWeight: "bold", fontSize: "0.75rem" }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    · <strong>{regionAvgData?.totalShipments ?? 0}</strong> delivered
                                </Typography>
                            </>
                        )}
                    </Box>

                    <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        disabled={isLoadingRegionAvg}
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        disabled={isLoadingRegionAvg}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => { setStartDate(""); setEndDate(""); }}
                        disabled={!startDate && !endDate}
                    >
                        Clear
                    </Button>

                    <Box sx={{ flexGrow: 1 }} />

                    <TableFilter
                        searchLabel="Search by branch name or code"
                        disabled={branchLoading || branchFetching}
                    />

                    <Tooltip title="Export to Excel">
                        <span>
                            <IconButton
                                color="primary"
                                onClick={handleExcelExport}
                                disabled={branchLoading || branchFetching || !branchData?.data?.length}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "primary.main",
                                    borderRadius: 1,
                                    p: "6px",
                                    "&.Mui-disabled": {
                                        borderColor: "action.disabled",
                                    },
                                }}
                            >
                                <Download fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>

                </Box>

                <Divider />

                <AppTable
                    columns={columns}
                    data={branchData?.data ?? []}
                    progressPending={branchLoading || branchFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={branchData?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />

            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default ShipmentDeliveryReport;